import type { ElementNode } from '@payloadcms/richtext-slate';
import classNames from 'classnames';
import type { ElementType, ReactNode } from 'react';

import { PayloadSlateRenderer } from '@/components/PayloadSlateRenderer/PayloadSlateRenderer';
import type { ContentBlock, DataAttributes } from '@/types';
import { Byline } from '@/ui/Byline/Byline';
import { TrackedLink } from '@/ui/Content/TrackedLink';
import { InlineQuote } from '@/ui/Quote/InlineQuote';
import { Section } from '@/ui/Section/Section';
import { TitleHighlighter } from '@/ui/TitleHighlighter/TitleHighlighter';
import { slugify } from '@/util/strings/slugify';

import { Media } from './Media';

import styles from './Content.module.scss';

interface Props extends DataAttributes {
    title: ContentBlock['title'];
    backgroundColor?: ContentBlock['bgColor'];
    body?: ContentBlock['body'];
    bodyText?: string;
    hasByline?: ContentBlock['has_byline'];
    hasMedia?: ContentBlock['has_media'];
    hasActions?: ContentBlock['has_actions'];
    mediaPosition?: ContentBlock['media_position'];
    byline?: ContentBlock['byline'];
    hasVideo?: ContentBlock['has_video'];
    video?: ContentBlock['video'];
    className?: string;
    classes?: {
        inner?: string;
        byline?: string;
        title?: string;
    };
    customActions?: ReactNode;
    media?: ContentBlock['media'];
    quote?: ContentBlock['quote'];
    actions?: ContentBlock['actions'];
    isHero?: boolean;
    useChipTextAsH1?: boolean;
}

export function Content({
    title,
    actions,
    backgroundColor = 'white',
    body,
    bodyText,
    byline,
    className,
    classes,
    mediaPosition = 'below',
    customActions,
    hasActions,
    hasByline,
    hasMedia,
    media,
    quote,
    isHero,
    useChipTextAsH1 = false,
    hasVideo,
    video,
    ...attributes
}: Props) {
    const isUsedAsSectionTitle = !hasMedia && !hasByline && !isHero;

    // for SEO purposes we're doing magic with the titles. Sometimes h1, sometimes h3
    let TitleTag = (isHero ? 'h1' : 'h2') as ElementType;
    let ChipTag = (isHero ? 'h2' : 'h3') as ElementType;
    if (useChipTextAsH1) {
        TitleTag = 'h2';
        ChipTag = 'h1';
    }

    return (
        <Section
            {...attributes}
            data-block={slugify(title)}
            data-block-is-hero={isHero}
            data-block-media-position={mediaPosition}
            primary={backgroundColor}
            isHero={isHero}
            className={classNames(styles.container, className, {
                [styles.hero]: isHero,
            })}
        >
            <div className={classNames(styles.inner, classes?.inner)}>
                <div className={styles.block}>
                    <div
                        className={classNames(styles.layout, {
                            [styles.mediaLeft]: mediaPosition === 'left',
                            [styles.mediaRight]: mediaPosition === 'right',
                        })}
                    >
                        <div className={styles.content}>
                            {hasByline && byline?.text && (
                                <Byline
                                    className={classNames(styles.byline, classes?.byline)}
                                    isHero={isHero}
                                    text={byline.text}
                                    type={byline.type}
                                    url={byline.url?.path}
                                    TagName={ChipTag}
                                />
                            )}
                            <TitleTag
                                className={classNames(styles.title, classes?.title, {
                                    [styles.sectionTitle]: isUsedAsSectionTitle,
                                })}
                                data-block-title={slugify(title)}
                            >
                                <TitleHighlighter>{title}</TitleHighlighter>
                            </TitleTag>
                            {body && (
                                <PayloadSlateRenderer
                                    data-block-body
                                    className={styles.body}
                                    nodes={body as ElementNode[]}
                                />
                            )}

                            {bodyText && <div className={styles.body}>{bodyText}</div>}
                            {quote?.quote && mediaPosition !== 'below' && (
                                <InlineQuote
                                    data-block-quote
                                    author={quote.author}
                                    avatar={quote.avatar}
                                    body={quote.quote as ElementNode[]}
                                    className={styles.quote}
                                    company={quote.customer}
                                    companyUrl={quote.customerUrl}
                                    reviewScore={quote.reviewScore}
                                    storyPath={quote.caseStudy?.path}
                                />
                            )}
                            {hasActions && actions && actions.length > 0 && (
                                <div className={styles.actions} data-block-actions>
                                    {actions
                                        .filter(
                                            ({ link }) =>
                                                (link.type === 'reference' && link.reference) ||
                                                (link.type !== 'reference' && link.url),
                                        )
                                        .map(({ id, link }, index) => (
                                            <TrackedLink key={id ?? index} link={link} />
                                        ))}
                                </div>
                            )}

                            {customActions}
                        </div>
                        <Media
                            hasMedia={hasMedia}
                            hasVideo={hasVideo}
                            media={media}
                            title={title}
                            video={video}
                        />
                    </div>
                </div>
            </div>
        </Section>
    );
}
